import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';

import { ReactComponent as NavigateArrow } from '../../../../assets/icons/dashboard/navigate-arrow.svg';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import { TooltipIcon } from '../../../../shared-components/tooltip/TooltipIcon';
import dashboard from '../../../../utils/api/dashboard';

/**
 * Returns color based on status
 * @param {string} status The performance status.
 * @param {number} value - The value
 * @returns {string} The color hex code.
 */
const getBarColor = (status: string, value: number) => {
  if (value === 0) return '#E2E8F0'; // Light gray for 0 values
  switch (status) {
    case 'optimal':
      return '#2E9E3A';
    case 'subOptimal':
      return '#F79009';
    case 'critical':
      return '#EF4444';
    default:
      return '#2E9E3A';
  }
};

/**
 * Returns CSS class modifier based on status and value.
 * @param {string} status - The performance status.
 * @param {number} value - The value.
 * @returns {string} The CSS class name.
 */
const getValueColorClass = (status: string, value: number): string => {
  if (value === 0) return 'zero';
  switch (status) {
    case 'optimal':
      return 'optimal';
    case 'subOptimal':
      return 'sub-optimal';
    case 'critical':
      return 'critical';
    default:
      return 'optimal';
  }
};

/**
 * Skeleton Chart sub-component for empty data state
 * @returns {any} The skeleton chart component.
 */
function SkeletonChart() {
  const skeletonData = Array.from({ length: 8 }).map((_, index) => ({
    name: `Loading ${index}`,
    value: 100,
  }));

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={skeletonData}
        layout="vertical"
        margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
        barSize={20}>
        <XAxis type="number" hide domain={[0, 100]} />
        <YAxis
          dataKey="name"
          type="category"
          axisLine={false}
          tickLine={false}
          tick={false}
          width={80}
        />
        <Bar dataKey="value" radius={[0, 4, 4, 0]} fill="#F1F5F9" isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Health Score Chart sub-component
 * @param {object} properties - The properties
 * @param {any[]} properties.data - The data prop
 * @returns {any} The health score chart component.
 */
function HealthScoreChart({ data }: { data: any[] }) {
  /**
   * Custom tooltip for the bar chart
   * @param {TooltipProps<number, string>} properties - The tooltip properties
   * @param {boolean} properties.active - The active prop
   * @param {any} properties.payload - The payload prop
   * @returns {any} The custom tooltip element.
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const entry = payload[0]?.payload;
      return (
        <div className="station-performance-tooltip">
          <p className="station-performance-tooltip-title">{entry?.name}</p>
          <p
            className={`station-performance-tooltip-value ${getValueColorClass(
              entry?.status,
              entry?.value,
            )}`}>
            {entry?.value}
          </p>
        </div>
      );
    }
    return null;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
        barSize={20}>
        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
        <XAxis type="number" hide />
        <YAxis
          dataKey="name"
          type="category"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 13, fill: '#64748B' }}
          width={80}
        />
        <Bar dataKey="renderValue" radius={[0, 4, 4, 0]}>
          {data.map((entry, index) => (
            <Cell key={`hs-cell-${index}`} fill={getBarColor(entry.status, entry.value)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Output Chart sub-component
 * @param {object} properties - The properties
 * @param {any[]} properties.data - The data prop
 * @returns {any} The output chart component.
 */
function OutputChart({ data }: { data: any[] }) {
  /**
   * Custom tooltip for the bar chart
   * @param {TooltipProps<number, string>} properties - The tooltip properties
   * @param {boolean} properties.active - The active prop
   * @param {any} properties.payload - The payload prop
   * @returns {any} The custom tooltip element.
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const entry = payload[0]?.payload;
      return (
        <div className="station-performance-tooltip">
          <p className="station-performance-tooltip-title">{entry?.name}</p>
          <p className="station-performance-tooltip-value output">
            {`${Number(entry?.value ?? 0).toFixed(3)} kW`}
          </p>
        </div>
      );
    }
    return null;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
        barSize={20}>
        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
        <XAxis type="number" hide />
        <YAxis
          dataKey="name"
          type="category"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 13, fill: '#64748B' }}
          width={80}
        />
        <Bar dataKey="renderValue" radius={[0, 4, 4, 0]}>
          {data.map((entry, index) => (
            <Cell key={`out-cell-${index}`} fill={entry.value === 0 ? '#E2E8F0' : '#2E9E3A'} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

interface NewStationPerformanceProperties {
  startDate?: string;
  endDate?: string;
  filters?: {
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  };
}

/**
 * NewStationPerformanceChart component
 * Displays a horizontal bar chart for station health/output (Redesigned Version)
 * @param {NewStationPerformanceProperties} properties - The properties
 * @param {string} [properties.startDate] - The startDate prop
 * @param {string} [properties.endDate] - The endDate prop
 * @param {object} [properties.filters] - The filters prop
 * @returns {any} The station performance chart component.
 */
function NewStationPerformanceChart({
  startDate,
  endDate,
  filters,
}: Readonly<NewStationPerformanceProperties>) {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeTab] = useState<'healthScore' | 'output'>('output');
  const [graphData, setGraphData] = useState<any[]>([]);

  /**
   * Fetches chart data based on the active tab and filters.
   * @returns {Promise<void>} Promise resolving when data is fetched.
   */
  const fetchChartData = async () => {
    setIsLoading(true);
    try {
      const query: any = {
        accountCustomId: filters?.facilityIds,
        stationIds: filters?.stationIds,
        hardRefresh: true,
      };
      if (startDate) {
        query.startDate = startDate;
      }
      if (endDate) {
        query.endDate = endDate;
      }
      let response;

      if (activeTab === 'healthScore') {
        response = await dashboard.getStationHealthScore({ ...query, type: 'healthScore' });
      } else {
        response = await dashboard.getStationHealthScore({
          ...query,
          type: 'totalUsagePowerConsume',
        });
      }

      if (response?.status === 200 && Array.isArray(response?.data?.results)) {
        const mappedData = response.data?.results?.map((item: any) => {
          const rawValue =
            activeTab === 'healthScore'
              ? (item.healthScore ?? item.value ?? 0)
              : (item.performanceScore ?? item.value ?? 0);
          const parsedValue =
            activeTab === 'healthScore' ? rawValue : Number(Number(rawValue).toFixed(3));
          return {
            name: item.stationName || item.name || '',
            value: parsedValue,
            renderValue: parsedValue === 0 ? 2 : parsedValue, // Small block for 0 values
            status: item.status || 'optimal',
          };
        });
        setGraphData(mappedData);
      } else {
        setGraphData([]);
      }
    } catch (error) {
      console.error('Error fetching station performance data:', error);
      setGraphData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchChartData();
  }, [filters, activeTab, startDate, endDate]);

  /**
   * Handles navigation to the approved charging stations page.
   * @returns {void}
   */
  const handleNavigate = (): void => {
    navigate('/ev-admin/operations/charging-station/approved');
  };

  // Calculate dynamic height based on number of bars (approx 45px per bar)
  const chartHeight = Math.max(350, graphData.length * 45);

  return (
    <div className="new-station-performance-chart-wrap">
      <div className="new-station-performance-chart-header">
        <div className="header-top">
          <div className="header-title">
            <div className="title-wrapper">
              <h6>{t('stationPerformance')}</h6>
              <TooltipIcon
                text={t('stationPerformanceChart')}
                infoColor="info"
              />
            </div>
            <NavigateArrow className="navigate-arrow" onClick={handleNavigate} />
          </div>
        </div>

        <div className="header-bottom">
          <div className="chart-legend">
            {activeTab === 'healthScore' && (
              <>
                <div className="legend-item">
                  <span className="dot optimal" />
                  <span>{t('optimal')}</span>
                </div>
                <div className="legend-item">
                  <span className="dot sub-optimal" />
                  <span>{t('subOptimal')}</span>
                </div>
                <div className="legend-item">
                  <span className="dot critical" />
                  <span>{t('critical')}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="station-performance-loader">
          <BatteryLoader />
        </div>
      ) : (
        <div className="station-performance-chart-scroll-container" dir="ltr">
          <div
            className="station-performance-chart-container"
            dir="ltr"
            style={{ height: chartHeight }}>
            {graphData.length === 0 && <SkeletonChart />}
            {graphData.length > 0 && activeTab === 'healthScore' && (
              <HealthScoreChart data={graphData} />
            )}
            {graphData.length > 0 && activeTab === 'output' && <OutputChart data={graphData} />}
          </div>
        </div>
      )}
    </div>
  );
}

NewStationPerformanceChart.defaultProps = {
  startDate: '',
  endDate: '',
  filters: {
    facilityIds: [],
    stationIds: [],
    deviceIds: [],
    type: ['commercial'],
  },
};
export default NewStationPerformanceChart;
